import { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon, MinusIcon, PhoneIcon, PlusIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { relatedFilters } from '../data/products';
import { SiteLink } from '../components/SiteLink';
import { ProductCard } from '../components/ProductCard';
const TITLE = 'Viking Spas Replacement Filter | Pegasus Spas';
const DESCRIPTION = 'Shop the Viking Spas replacement filter, SKU 14294, from Pegasus Spas. Review dimensions and confirm fit before ordering.';
const CANONICAL = 'https://www.pegasus-spas.com/shop/product/viking-spas-replacement-filter';
const PRODUCT = {
  name: 'Viking Spas replacement filter',
  sku: '14294',
  gtin: '0678285260091',
  price: '$56.00',
  availability: 'In Stock',
  image: 'https://www.pegasus-spas.com/uploaded/images/Filters/6ch940.jpg400x400.jpg',
  alt: 'Viking Spas replacement filter cartridge with blue end cap'
};
const specs = [{
  term: 'Filter area',
  value: '45 sq. ft.'
}, {
  term: 'Length',
  value: '8 1/4 in.'
}, {
  term: 'Outside diameter',
  value: '6 in.'
}, {
  term: 'Top',
  value: 'Handle'
}, {
  term: 'Bottom',
  value: '1 1/2-in. SAE'
}, {
  term: 'Brand compatibility',
  value: 'Viking Spas'
}, {
  term: 'SKU',
  value: '14294'
}, {
  term: 'UPC/GTIN',
  value: '0678285260091'
}];
const fitSteps = ['Start with the current filter: bring it in or take clear photos of its label, top, bottom and overall shape.', 'Compare the cartridge length, diameter and openings.', 'Contact us if any measurement or fit detail is uncertain.'];
type TabId = 'details' | 'specs';
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function ProductDetailPage() {
  const [quantity, setQuantity] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [status, setStatus] = useState('');
  const [tab, setTab] = useState<TabId>('details');
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({
    details: null,
    specs: null
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  const selectTab = (next: TabId) => {
    setTab(next);
    tabRefs.current[next]?.focus();
  };
  const tabClass = (id: TabId) => `inline-flex min-h-[48px] items-center border-b-2 px-4 text-[16px] font-bold transition-colors duration-150 ease-out ${tab === id ? 'border-pegasus-magenta text-ink' : 'border-transparent text-ink/60 hover:text-ink'}`;
  return <main data-id="element-1711" data-mp="r9bjgzksncdw">
      {/* Breadcrumb */}
      <section aria-label="Breadcrumb" className="w-full border-b border-ink/10 bg-white" data-id="element-1712" data-mp="mh0idtebhkjr">
        <div className="mx-auto max-w-container px-5 py-4 sm:px-8" data-id="element-1713" data-mp="hhxam16yd083">
          <nav aria-label="Breadcrumb" data-id="element-1714" data-mp="hi725t7nbffw">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px] text-ink/70" data-id="element-1715" data-mp="cj4791ldqpln">
              <li data-id="element-1716" data-mp="1a6co2jafrahb">
                <SiteLink to={routes.home} className="underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-1717" data-mp="1a6us423caszs">
                  Home
                </SiteLink>
              </li>
              <li aria-hidden="true" data-id="element-1718" data-mp="w7czfbmxavl5">/</li>
              <li data-id="element-1719" data-mp="w6a852lour4p">
                <SiteLink to={routes.supplies} className="underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-1720" data-mp="w8wfb14o34me">
                  Shop Online
                </SiteLink>
              </li>
              <li aria-hidden="true" data-id="element-1721" data-mp="18m9p3mv7ywx8">/</li>
              <li data-id="element-1722" data-mp="1dfckm0jk4gg2">
                <SiteLink to={routes.filters} className="underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-1723" data-mp="1dfuq7ns4070n">
                  Filters
                </SiteLink>
              </li>
              <li aria-hidden="true" data-id="element-1724" data-mp="1rfvr338djeg4">/</li>
              <li aria-current="page" className="font-medium text-ink" data-id="element-1725" data-mp="1rd15v9hjdpln">
                Viking Spas Replacement Filter
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Purchase area */}
      <section aria-labelledby="product-h1" className="w-full bg-white" data-id="element-1726" data-mp="1mfhhhgr4jwfz">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-1727" data-mp="1mhltklz6g1rc">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-1728" data-mp="6xperok656o3">
            {/* Gallery */}
            <div className="lg:col-span-6" data-id="element-1729" data-mp="ayhpkm8ovjmh">
              <div className="aspect-square w-full overflow-hidden rounded-[6px] border border-ink/15 bg-white p-6" data-id="element-1730" data-mp="3gjtbjfiq517">
                <img src={PRODUCT.image} alt={PRODUCT.alt} className="h-full w-full object-contain" decoding="async" data-id="element-1731" data-mp="3umcmtmsf155b" />
              </div>

              <ul className="mt-3 flex gap-3" data-id="element-1732" data-mp="8gxs0av8c2mk">
                <li data-id="element-1733" data-mp="3gvg5wt7uqce3">
                  <button type="button" aria-label="View image 1 of Viking Spas replacement filter" aria-current="true" className="block h-20 w-20 overflow-hidden rounded-[6px] border-2 border-pegasus-magenta bg-white p-1.5" data-id="element-1734" data-mp="3gx0zogmcdraw">
                    <img src={PRODUCT.image} alt="" className="h-full w-full object-contain" loading="lazy" decoding="async" data-id="element-1735" data-mp="3ltrfr3y5cmwb" />
                  </button>
                </li>
              </ul>
            </div>

            {/* Purchase panel */}
            <div className="lg:col-span-6" data-id="element-1736" data-mp="3mv2t509otz8y">
              <SiteLink to={routes.filters} className="text-[13px] font-bold uppercase text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1737" data-mp="3mtrneuwomhf7">
                Filters
              </SiteLink>

              <h1 id="product-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[36px] lg:leading-[44px]" data-id="element-1738" data-mp="33y1lt8snvn1u">
                Viking Spas Replacement Filter
              </h1>

              <p className="mt-2 text-[15px] text-ink/60" data-id="element-1739" data-mp="3dt39s1b7aj2u">SKU {PRODUCT.sku}</p>

              <p className="mt-4 text-[28px] font-semibold leading-none text-ink" data-id="element-1740" data-mp="o5vwiru3w0ge">
                {PRODUCT.price}
              </p>
              <p className="mt-2 text-[15.5px] font-medium text-pine" data-id="element-1741" data-mp="2dn9idy83zkmw">{PRODUCT.availability}</p>

              <p className="mt-5 max-w-xl text-body text-ink/80 sm:text-body-lg" data-id="element-1742" data-mp="28o6dcds4peex">
                A 45-square-foot replacement filter cartridge for compatible Viking Spas. Compare
                the measurements below with your current filter before ordering.
              </p>

              <div className="mt-5 max-w-xl rounded-[6px] bg-water p-4" data-id="element-1743" data-mp="1ywaev1m4rma6">
                <p className="text-[16px] font-semibold text-ink" data-id="element-1744" data-mp="1uycpnysd106s">Confirm the fit</p>
                <p className="mt-1 text-[16px] leading-6 text-ink/80" data-id="element-1745" data-mp="1pwnaq820o2df">
                  Filter fit depends on the spa and existing cartridge. Bring the old filter or send
                  clear photos of its label, top, bottom and overall shape. If model information
                  would help, we can show you where to find it.
                </p>
                <a href={business.phoneHref} className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1746" data-mp="3adikfq8ij8fx">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1747" data-mp="2afcnysyh5jpp" />
                  Call or text {business.phone}
                </a>
              </div>

              {/* Quantity + options slot */}
              <div className="mt-6 max-w-xl border-t border-ink/15 pt-5" data-id="element-1748" data-mp="20g3mlf1a52tb">
                <div className="flex flex-wrap items-center gap-3" data-id="element-1749" data-mp="25hsxmuoi5ghe">
                  <label htmlFor="detail-qty" className="text-[15px] font-semibold text-ink" data-id="element-1750" data-mp="1rh9rxu7iemie">
                    Quantity
                  </label>
                  <div className="flex items-center rounded-btn border border-ink/25" data-id="element-1751" data-mp="1vj3q9vu8fefe">
                    <button type="button" aria-label="Decrease quantity" onClick={() => setQuantity(value => Math.max(1, value - 1))} className="flex h-12 w-12 items-center justify-center text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-1752" data-mp="3lwcebj19hoh3">
                      <MinusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1753" data-mp="1nysvb4733aud" />
                    </button>
                    <input id="detail-qty" type="number" min={1} inputMode="numeric" value={quantity} onChange={event => {
                    const next = Number(event.target.value);
                    setQuantity(Number.isFinite(next) && next >= 1 ? Math.floor(next) : 1);
                  }} className="h-12 w-14 border-x border-ink/25 bg-white text-center text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pegasus-blue/40" data-id="element-1754" data-mp="1j7uabrb5hoks" />
                    <button type="button" aria-label="Increase quantity" onClick={() => setQuantity(value => value + 1)} className="flex h-12 w-12 items-center justify-center text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-1755" data-mp="7mdtttxoboa9">
                      <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1756" data-mp="hiyognzkq72n" />
                    </button>
                  </div>
                </div>

                <button type="button" onClick={() => {
                setCartCount(count => count + quantity);
                setStatus('Added to preview cart');
              }} className="mt-5 inline-flex min-h-[52px] w-full items-center justify-center rounded-btn bg-pegasus-magenta px-6 text-[17px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83] sm:w-auto sm:px-10" data-id="element-1757" data-mp="1e7wke9j28b1e">
                  Add to Cart
                </button>

                <p aria-live="polite" className="mt-3 min-h-[24px] text-[15.5px] text-ink/70" data-id="element-1758" data-mp="20hd3nu3ickfj">
                  {status && cartCount > 0 ? `${status} — ${cartCount} item${cartCount === 1 ? '' : 's'} (preview only)` : ''}
                </p>

                <p className="mt-1 text-[15.5px] text-ink/70" data-id="element-1759" data-mp="2egwamjkl1pq9">
                  Questions before ordering? Call or text us.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Details + specifications */}
      <section aria-labelledby="info-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1760" data-mp="2odhhlag5xawq">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1761" data-mp="2t7vczcutri1e">
          <h2 id="info-heading" className="sr-only" data-id="element-1762" data-mp="2y4c0c4u79sdf">
            Product information
          </h2>

          <div role="tablist" aria-label="Product information" className="flex border-b border-ink/15" data-id="element-1763" data-mp="328a3cqmnbnbr">
            <button ref={node => {
            tabRefs.current.details = node;
          }} type="button" role="tab" id="tab-details" aria-selected={tab === 'details'} aria-controls="panel-details" tabIndex={tab === 'details' ? 0 : -1} onClick={() => setTab('details')} onKeyDown={event => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
              event.preventDefault();
              selectTab('specs');
            }
          }} className={tabClass('details')} data-id="element-1764" data-mp="375tfnkzs9q12">
              Product Details
            </button>
            <button ref={node => {
            tabRefs.current.specs = node;
          }} type="button" role="tab" id="tab-specs" aria-selected={tab === 'specs'} aria-controls="panel-specs" tabIndex={tab === 'specs' ? 0 : -1} onClick={() => setTab('specs')} onKeyDown={event => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
              event.preventDefault();
              selectTab('details');
            }
          }} className={tabClass('specs')} data-id="element-1765" data-mp="1usv7zj9ujpyd">
              Specifications
            </button>
          </div>

          <div role="tabpanel" id="panel-details" aria-labelledby="tab-details" hidden={tab !== 'details'} tabIndex={0} className="mt-6 max-w-3xl" data-id="element-1766" data-mp="1tao7r6zd3qk9">
            <p className="text-body text-ink/80 sm:text-body-lg" data-id="element-1767" data-mp="1kcuhods6xt5j">
              A 45-square-foot replacement filter cartridge for compatible Viking Spas. Compare the
              measurements in the Specifications tab with your current cartridge before ordering.
            </p>
          </div>

          <div role="tabpanel" id="panel-specs" aria-labelledby="tab-specs" hidden={tab !== 'specs'} tabIndex={0} className="mt-6 max-w-3xl" data-id="element-1768" data-mp="3ipuoyt8okv1t">
            <dl className="border-t border-ink/15" data-id="element-1769" data-mp="2k1turgxs31j9">
              {specs.map(spec => <div key={spec.term} className="flex flex-col gap-0.5 border-b border-ink/10 py-3 sm:flex-row sm:gap-6" data-id="element-1770" data-mp="2k3n4j53ou1vh">
                  <dt className="text-[16px] font-semibold text-ink sm:w-[200px] sm:shrink-0" data-id="element-1771" data-mp="2b2giyhm6m0vd">
                    {spec.term}
                  </dt>
                  <dd className="text-[16px] leading-6 text-ink/85" data-id="element-1772" data-mp="21aswkv4hfa0r">{spec.value}</dd>
                </div>)}
            </dl>
          </div>
        </div>
      </section>

      {/* Fitting help */}
      <section aria-labelledby="fit-heading" className="w-full bg-water" data-id="element-1773" data-mp="1rdeg4vik6l8g">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1774" data-mp="1nbjka6jed022">
          <h2 id="fit-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1775" data-mp="1ndns2zkf2j7m">
            Before You Order a Replacement Filter
          </h2>

          <ol className="mt-7 grid grid-cols-1 gap-x-10 gap-y-6 lg:grid-cols-3" data-id="element-1776" data-mp="6tsst1zphlf4">
            {fitSteps.map((step, index) => <li key={step} className="border-t border-ink/20 pt-4" data-id="element-1777" data-mp="bs42ncq03vxc" data-mpi={index}>
                <span className="block text-[15px] font-bold text-pegasus-magenta" data-id="element-1778" data-mp="grysre1ychhc">
                  0{index + 1}
                </span>
                <p className="mt-1.5 text-[16.5px] leading-6 text-ink/85" data-id="element-1779" data-mp="3f92tc5ny6iks">{step}</p>
              </li>)}
          </ol>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1780" data-mp="3p8legpflgaef">
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-1781" data-mp="3p7aaopm5f2nb">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1782" data-mp="31hobw39oupfu" />
              Call or Text {business.phone}
            </a>
            <SiteLink to={routes.spaCare} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1783" data-mp="2ph7tj4xikf5e">
              Spa Care &amp; FAQs
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related filters */}
      <section aria-labelledby="related-heading" className="w-full bg-white" data-id="element-1784" data-mp="26j59z5nqjaez">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1785" data-mp="2khv72lak1a3c">
          <h2 id="related-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1786" data-mp="2kfs6x8fou0kw">
            More Replacement Filters
          </h2>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1787" data-mp="3vzefcs2zs38r">
            {relatedFilters.map(product => <ProductCard key={product.sku} product={product} onAddToCart={amount => {
            setCartCount(count => count + amount);
            setStatus('Added to preview cart');
          }} data-id="element-1788" data-mp="1cpxzgnvgicvb" />)}
          </div>

          <SiteLink to={routes.filters} className="mt-8 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1789" data-mp="18o2p6tvjsp5e">
            View All Filters
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1790" data-mp="1rnqdtcp9ov7x" />
          </SiteLink>
        </div>
      </section>

      {/* Final navy CTA */}
      <section aria-labelledby="help-heading" className="w-full bg-ink" data-id="element-1791" data-mp="2fcj0eik01qpv">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1792" data-mp="2k4j84x6b9nx0">
          <div className="max-w-3xl" data-id="element-1793" data-mp="k1rs5l6wvssy">
            <p className="text-[14px] font-bold uppercase text-white/70" data-id="element-1794" data-mp="k2tdcihn5voe">Local Product Help</p>
            <h2 id="help-heading" className="mt-2 text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-1795" data-mp="fa0ay19nazfh">
              Not Sure This Is the Right Filter?
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-1796" data-mp="5eggljaqc8su">
              Bring the current filter or clear photos to the Brooklyn showroom, or call the Pegasus
              team before ordering.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1797" data-mp="t2g66fwb1t5u">
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1798" data-mp="t0mxyw61n0vq">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1799" data-mp="39qag7efvcjg0" />
              Call or Text {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1800" data-mp="1p6yeyxexx6n3">
              Visit the Showroom
            </SiteLink>
          </div>
        </div>
      </section>
    </main>;
}